import { Component, HostListener, OnInit } from '@angular/core';
import { AssociationMongo } from 'app/models/association';
import { APPLICATION_TYPE_LABELS, ApplicationType } from 'app/models/application';
import { ApplicationsService } from 'app/services/applications.service';
import { AssociationsService } from 'app/services/associations.service';
import { Alert } from 'app/shared/alerts/simple-message/simple-message.component';
import { Logger } from 'app/utilities/logger';
import { SessionStorage } from 'app/storage/session.storage';
import { SopStorage } from 'app/storage/sop.storage';
import { AssociationHelper } from 'app/storage/association.storage';
import { catchError, EMPTY, forkJoin, lastValueFrom, of } from 'rxjs';
import { clone } from 'app/utilities/helper';
import { TOPIC_GROUPS } from 'app/models/epe-topics';

interface TopicMatch { high: number; middle: number; low: number; }
export interface EpeWithMatch { epe: any; match: TopicMatch; showSeparator?: boolean; }

enum AssociationStatusFilterEnum {
  all, open, identified, selected, approved, not_approved, completed, closed
}

const ALLOWED_STATUS = ['selected', 'approved', 'not_approved', 'completed', 'closed'];

const SERVICE_TYPES: ApplicationType[] = ['service1', 'service2'];

@Component({
  selector: 'association',
  templateUrl: './association.component.html',
  styleUrls: ['../epes/epes.component.scss', './association.component.scss', '../sop.component.scss']
})
export class AssociationComponent implements OnInit {
  loading = true;
  associations: AssociationMongo[] | null = null;
  allProjects: any[] = [];
  epes: any[] | null = [];
  selected: string = '';

  AssociationStatusFilter = AssociationStatusFilterEnum;
  filter: AssociationStatusFilterEnum = AssociationStatusFilterEnum.all;

  activeService: ApplicationType = 'service1';
  readonly SERVICE_TYPES = SERVICE_TYPES;
  readonly SERVICE_LABELS = APPLICATION_TYPE_LABELS;

  screenWidth = window.innerWidth;

  @HostListener('window:resize', ['$event'])
  onResize() { this.screenWidth = window.innerWidth; }

  sortBy = '';
  sortDirection = 1;

  showAlert = false;
  alertMessage = '';
  alertType: Alert = Alert.SUCCESS;

  search?: string;

  constructor(
    private applicationsService: ApplicationsService,
    private serviceMongo: AssociationsService,
    private cache: SopStorage,
    public auth: SessionStorage,
    private logger: Logger,
    private helper: AssociationHelper,
  ) {}

  ngOnInit(): void {
    this.helper.clear();

    const cachedProjects = this.cache.getProjects();
    const cachedEpes     = this.cache.getEpes();

    forkJoin({
      projects:     !cachedProjects ? this.applicationsService.getAll({ include_user: true }) : of(null),
      epes:         !cachedEpes     ? this.applicationsService.getAll({ type: 'epe', include_user: true }) : of(null),
      associations: !this.associations ? this.serviceMongo.getAll() : of(null),
    }).pipe(
      catchError(() => {
        this.addAlert('Error loading associations data.', Alert.ERROR);
        return EMPTY;
      })
    ).subscribe(then => {
      if (then.projects) {
        this.allProjects = then.projects
          .filter((a: any) => SERVICE_TYPES.includes(a.application_type))
          .map((app: any) => ({
            ...(app.form_data  || {}),
            ...(app.user_info  || {}),
            ...app,
            id:             app.user_id ?? app.id,
            application_id: app._id?.['$oid'] ?? app._id,
            sop_status:     app.status,
          }));
        this.cache.setProjects(this.allProjects);
      } else {
        this.allProjects = (cachedProjects || []).filter((p: any) =>
          SERVICE_TYPES.includes(p.application_type)
        );
      }

      if (then.epes) {
        const mapped = then.epes.map((app: any) => ({
          ...(app.form_data || {}),
          ...(app.user_info || {}),
          ...app,
          id:             app.user_id ?? app.id,
          application_id: app._id?.['$oid'] ?? app._id,
          sop_status:     app.status,
        }));
        this.cache.setEpes(mapped);
        this.epes = clone(mapped).filter((x: any) => x.status === 'voted');
      } else {
        this.epes = clone(cachedEpes || []).filter((x: any) => x.status === 'voted');
      }

      if (then.associations) {
        this.associations = then.associations;
        this.helper.setAssociations(then.associations);
      }

      this.epes?.forEach(epe => this.updateEpeScore(epe));
      this.loading = false;
    });
  }

  async updateEpeScore(epe: any): Promise<void> {
    const appId = epe._id?.['$oid'] ?? epe._id;
    if (!appId) return;
    try {
      const scores = await lastValueFrom(this.applicationsService.getScore(appId), { defaultValue: null });
      if (scores) { epe.score = scores.score; epe.nvotes = scores.nvotes; }
    } catch {}
  }

  // ── Service tabs ──────────────────────────────────────────────────────────────

  setService(type: ApplicationType): void {
    this.activeService = type;
    this.selected = '';
    this.selectedSuggestionId = '';
    this.filter = AssociationStatusFilterEnum.all;
    this.search = '';
  }

  get projectsForService(): any[] {
    return this.allProjects.filter(p =>
      p.application_type === this.activeService && p.status === 'eligible'
    );
  }

  // ── Association status helpers ─────────────────────────────────────────────────

  getStatus(project_id: string): string {
    const pa = this.associations?.filter(x => x['project_application_id'] == project_id);
    if (!pa || pa.length === 0) return '';
    for (const s of ['closed','completed','approved','not_approved','selected','identified','open']) {
      if (pa.some(x => x['association_status'] === s)) return s;
    }
    return '';
  }

  isCurrentEpe(status: string | undefined): boolean {
    return !['open','waiting','not_approved',undefined].includes(status);
  }

  // ── Project list ──────────────────────────────────────────────────────────────

  getProjects(): any[] {
    let list = this.projectsForService;
    if (this.auth.isProject()) return [];

    if (this.search && this.search.length > 0) {
      const q = this.search.toLowerCase();
      list = list.filter(x =>
        (x.username ?? '').toLowerCase().includes(q) ||
        (x.project_acronym ?? '').toLowerCase().includes(q) ||
        ((x.name ?? '') + ' ' + (x.surname ?? '')).toLowerCase().includes(q)
      );
    }

    switch (this.filter) {
      case AssociationStatusFilterEnum.open:         return list.filter(x => this.getStatus(x.application_id) === 'open'         || this.getStatus(x.application_id) === '');
      case AssociationStatusFilterEnum.identified:   return list.filter(x => this.getStatus(x.application_id) === 'identified');
      case AssociationStatusFilterEnum.selected:     return list.filter(x => this.getStatus(x.application_id) === 'selected');
      case AssociationStatusFilterEnum.approved:     return list.filter(x => this.getStatus(x.application_id) === 'approved');
      case AssociationStatusFilterEnum.not_approved: return list.filter(x => this.getStatus(x.application_id) === 'not_approved');
      case AssociationStatusFilterEnum.completed:    return list.filter(x => this.getStatus(x.application_id) === 'completed');
      case AssociationStatusFilterEnum.closed:       return list.filter(x => this.getStatus(x.application_id) === 'closed');
      default: return list;
    }
  }

  // ── EPEs per project ──────────────────────────────────────────────────────────

  getProjectEpes(project_id: string): any[] {
    if (!this.associations || !this.epes) return [];
    const epeIds = this.associations
      .filter(x => x['project_application_id'] == project_id)
      .map(x => x['epe_application_id']);
    return [...this.epes]
      .filter(x => epeIds.includes(x.application_id))
      .map(x => {
        x.topics = this.getTopics(x, x.application_id, project_id);
        x['association_status'] = this.associations!.find(
          y => y['epe_application_id'] === x.application_id && y['project_application_id'] === project_id
        )?.['association_status'];
        return x;
      })
      .sort(this.compareEpes);
  }

  getTopics(epe: any, epe_id: string, project_id: string): object[] | undefined {
    if (!this.associations) return undefined;
    const a = this.associations.find(x => x['epe_application_id'] == epe_id && x['project_application_id'] == project_id);
    epe['low']    = a?.['low'];
    epe['middle'] = a?.['middle'];
    epe['high']   = a?.['high'];
    return a?.topics;
  }

  compareEpes(a: any, b: any): number {
    const isActive = (s: string) => !['open','waiting','not_approved',undefined].includes(s);
    if (isActive(a.association_status) && !isActive(b.association_status)) return -1;
    if (!isActive(a.association_status) && isActive(b.association_status)) return 1;
    if (a.high && !b.high) return -1; if (!a.high && b.high) return 1;
    if (a.middle && !b.middle) return -1; if (!a.middle && b.middle) return 1;
    if (a.low && !b.low) return -1; if (!a.low && b.low) return 1;
    if (a.score && b.score && a.score !== b.score) return b.score - a.score;
    return 0;
  }

  // ── Expand ────────────────────────────────────────────────────────────────────

  expand(id: string): void {
    if (id === this.selected) { this.selected = ''; this.selectedSuggestionId = ''; return; }
    const pa = this.associations?.filter(x => x['project_application_id'] == id) || [];
    const epeId = pa.find(x => this.isCurrentEpe(x['association_status']))?.['epe_application_id'];
    this.helper.setSelectedEpe(epeId ?? '');
    this.selectedSuggestionId = epeId ?? '';
    const meeting = pa.find(x => x['epe_application_id'] === epeId && x['project_application_id'] === id)?.meeting;
    if (meeting) this.helper.setMeetingForm(meeting as number);
    this.helper.setSelectedProject(id);
    this.selected = id;
    if (!this.matchCache.has(id)) {
      const project = this.allProjects.find(p => p.application_id === id);
      if (project) this.matchCache.set(id, this.computeMatchingEpes(project));
    }
  }

  // ── Topic labels ──────────────────────────────────────────────────────────────

  getTopicLabels(project: any): string[] {
    const topics: string[] = project.form_data?.topics || project.topics || [];
    const labels: string[] = [];
    for (const value of topics) {
      if (typeof value !== 'string') continue;
      let found = false;
      for (const group of TOPIC_GROUPS) {
        for (const sub of group.subGroups) {
          if (sub.value === value) { labels.push(`${group.label} > ${sub.label}`); found = true; break; }
          const child = sub.children.find(c => c.value === value);
          if (child) { labels.push(`${group.label} > ${sub.label} > ${child.label}`); found = true; break; }
        }
        if (found) break;
      }
      if (!found) labels.push(value);
    }
    return labels;
  }

  // ── Status UI helpers ─────────────────────────────────────────────────────────

  getStatusLabel(status: string): string {
    const map: Record<string, string> = {
      '': 'Open', open: 'Open', identified: 'Identified', selected: 'Selected',
      approved: 'Approved', not_approved: 'Not Approved', completed: 'Completed', closed: 'Closed',
    };
    return map[status] || status;
  }

  getStatusBadgeClass(status: string): string {
    const map: Record<string, string> = {
      '': 'label-default', open: 'label-default', identified: 'label-info',
      selected: 'label-primary', approved: 'label-success', not_approved: 'label-danger',
      completed: 'label-warning', closed: 'label-default',
    };
    return map[status] || 'label-default';
  }

  getBtnText(project_id: string): string {
    const s = this.getStatus(project_id);
    const map: Record<string, string> = {
      '': 'Start Association', open: 'Association',
      identified: 'View Identified', selected: 'View Selected',
      approved: 'View Association', not_approved: 'View details',
      completed: 'View details', closed: 'View details',
    };
    return map[s] || 'View';
  }

  // ── Sort ──────────────────────────────────────────────────────────────────────

  changeSort(property: string): void {
    this.sortDirection = this.sortBy === property ? -this.sortDirection : 1;
    this.sortBy = property;
  }

  sortedProjects(): any[] {
    const list = this.getProjects();
    const dir = this.sortDirection;
    switch (this.sortBy) {
      case 'name':        return [...list].sort((a, b) => dir * (a.name ?? '').localeCompare(b.name ?? ''));
      case 'surname':     return [...list].sort((a, b) => dir * (a.surname ?? '').localeCompare(b.surname ?? ''));
      case 'projectName': return [...list].sort((a, b) => dir * (a.full_project_name ?? '').trimStart().localeCompare((b.full_project_name ?? '').trimStart()));
      case 'acronym':     return [...list].sort((a, b) => dir * (a.project_acronym ?? '').trimStart().localeCompare((b.project_acronym ?? '').trimStart()));
      default: return list;
    }
  }

  // ── Topic matching ────────────────────────────────────────────────────────────

  private matchCache = new Map<string, EpeWithMatch[]>();
  selectedSuggestionId: string = '';

  private computeTopicMatch(projectTopics: string[], epeTopics: string[]): TopicMatch {
    const epeExact = new Set(epeTopics);
    const epeSubGroups = new Set<string>();
    const epeGroups = new Set<string>();
    for (const v of epeTopics) {
      for (const g of TOPIC_GROUPS) {
        for (const s of g.subGroups) {
          if (s.children.some(c => c.value === v)) {
            epeSubGroups.add(s.value);
            epeGroups.add(g.label);
            break;
          }
        }
      }
    }
    let high = 0, middle = 0, low = 0;
    for (const v of projectTopics) {
      if (epeExact.has(v)) { high++; continue; }
      let placed = false;
      for (const g of TOPIC_GROUPS) {
        for (const s of g.subGroups) {
          if (s.children.some(c => c.value === v)) {
            if (epeSubGroups.has(s.value)) middle++;
            else if (epeGroups.has(g.label)) low++;
            placed = true;
            break;
          }
        }
        if (placed) break;
      }
    }
    return { high, middle, low };
  }

  private normalizeTopics(topics: any[]): string[] {
    return (topics || []).map((t: any) => typeof t === 'string' ? t : (t?.value ?? ''));
  }

  private computeMatchingEpes(project: any): EpeWithMatch[] {
    if (!this.epes) return [];
    const projectTopics: string[] = this.normalizeTopics(project.form_data?.topics);

    const scored = this.epes
      .map(epe => ({ epe, match: this.computeTopicMatch(projectTopics, this.normalizeTopics(epe.form_data?.topics)) }))
      .sort((a, b) => {
        const aScore = a.match.high * 100 + a.match.middle * 10 + a.match.low;
        const bScore = b.match.high * 100 + b.match.middle * 10 + b.match.low;
        if (bScore !== aScore) return bScore - aScore;
        return (b.epe.score || 0) - (a.epe.score || 0);
      });

    let separatorPlaced = false;
    return scored.map(item => {
      const hasMatch = item.match.high + item.match.middle + item.match.low > 0;
      const showSeparator = !hasMatch && !separatorPlaced;
      if (!hasMatch) separatorPlaced = true;
      return { ...item, showSeparator };
    });
  }

  getCachedMatchingEpes(project_id: string): EpeWithMatch[] {
    return this.matchCache.get(project_id) || [];
  }

  selectSuggestedEpe(epe_id: string): void {
    this.selectedSuggestionId = epe_id;
    this.helper.setSelectedEpe(epe_id);
  }

  getSelectedEpeName(project_id: string): string {
    if (!this.selectedSuggestionId) return '';
    const item = this.getCachedMatchingEpes(project_id).find(x => x.epe.application_id === this.selectedSuggestionId);
    if (!item) return '';
    const e = item.epe;
    return `${e.name || e.user_info?.name || ''} ${e.surname || e.user_info?.surname || ''}`.trim();
  }

  getMatchCount(project: any): number {
    return this.getCachedMatchingEpes(project.application_id)
      .filter(item => item.match.high + item.match.middle + item.match.low > 0).length;
  }

  isAssociated(project_id: string, epe_id: string): boolean {
    return this.associations?.some(a =>
      a['project_application_id'] == project_id && a['epe_application_id'] === epe_id
    ) ?? false;
  }

  getMatchBadgeClass(match: TopicMatch): string {
    if (match.high > 0) return 'badge-high';
    if (match.middle > 0) return 'badge-middle';
    if (match.low > 0) return 'badge-low';
    return 'badge-none';
  }

  getMatchLevelLabel(match: TopicMatch): string {
    if (match.high > 0) return `High · ${match.high}`;
    if (match.middle > 0) return `Medium · ${match.middle}`;
    if (match.low > 0) return `Low · ${match.low}`;
    return 'No match';
  }

  // ── Misc ──────────────────────────────────────────────────────────────────────

  changeFilter(s: AssociationStatusFilterEnum): void { this.filter = s; }

  reloadAssociations(): void {
    this.serviceMongo.getAll().pipe(
      catchError(() => {
        this.addAlert('Error reloading associations.', Alert.ERROR);
        return EMPTY;
      })
    ).subscribe((a: AssociationMongo[]) => {
      this.associations = a;
      this.helper.setAssociations(a);
    });
  }

  addAlert(message: string, type: Alert): void {
    this.alertMessage = message;
    this.alertType = type;
    this.showAlert = true;
  }
}
